import { createMutation } from '@/mutations'
import {
  addPlaygroundCapsuleItems,
  createPlaygroundCapsule,
  deletePlaygroundCapsule,
  deletePlaygroundCapsuleItem,
  detachOutfitsFromCapsule,
  submitCapsuleSurvey,
  type AddCapsuleItemsBody,
  type AddCapsuleItemsResponse,
  type CapsuleSurveySubmitBody,
  type CapsuleSurveySubmitResponse,
  type CreateCapsuleBody,
  type CreateCapsuleResponse,
  type DeleteCapsuleItemResponse,
  type DeleteCapsuleResponse,
  type DetachOutfitsResponse,
} from '@/features/style-space/api'
import { styleSpaceQueriesOf } from '@/queries/style-space'

const invalidateCapsuleReaders = styleSpaceQueriesOf(
  'capsules',
  'capsule',
  'outfits',
  'available-items',
)

export const useSubmitCapsuleSurveyMutation = () =>
  createMutation<CapsuleSurveySubmitResponse, CapsuleSurveySubmitBody>({
    mutationFn: async (body) => {
      const response = await submitCapsuleSurvey(body)
      return response.data
    },
  })

export const useCreateCapsuleMutation = () =>
  createMutation<CreateCapsuleResponse, CreateCapsuleBody>({
    mutationFn: async (body) => {
      const response = await createPlaygroundCapsule(body)
      return response.data
    },
    invalidateQueries: invalidateCapsuleReaders,
  })

export const useDeleteCapsuleMutation = () =>
  createMutation<DeleteCapsuleResponse, { capsuleId: number }>({
    mutationFn: async ({ capsuleId }) => {
      const response = await deletePlaygroundCapsule(capsuleId)
      return response.data
    },
    invalidateQueries: invalidateCapsuleReaders,
  })

export const useDeleteCapsuleItemMutation = () =>
  createMutation<DeleteCapsuleItemResponse, { capsuleId: number; capsuleItemId: number }>({
    mutationFn: async ({ capsuleId, capsuleItemId }) => {
      const response = await deletePlaygroundCapsuleItem(capsuleId, capsuleItemId)
      return response.data
    },
    invalidateQueries: invalidateCapsuleReaders,
  })

export const useDetachOutfitsFromCapsuleMutation = () =>
  createMutation<DetachOutfitsResponse, { capsuleId: number; outfitIds: number[] }>({
    mutationFn: async ({ capsuleId, outfitIds }) => {
      const response = await detachOutfitsFromCapsule(capsuleId, outfitIds)
      return response.data
    },
    invalidateQueries: invalidateCapsuleReaders,
  })

export const useAddCapsuleItemsMutation = () =>
  createMutation<AddCapsuleItemsResponse, { capsuleId: number; body: AddCapsuleItemsBody }>({
    mutationFn: async ({ capsuleId, body }) => {
      const response = await addPlaygroundCapsuleItems(capsuleId, body)
      return response.data
    },
    invalidateQueries: invalidateCapsuleReaders,
  })
